import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { collection, query, where, getDocs, orderBy, doc as firestoreDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import ReturnEquipmentModal from './ReturnEquipmentModal';

const MyBorrowedEquipmentPage = () => {
  const navigate = useNavigate();
  const { equipment, returnEquipment } = useEquipment();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [borrows, setBorrows] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedBorrow, setSelectedBorrow] = useState(null);

  useEffect(() => {
    const loadBorrows = async () => {
      if (!currentUser || !currentTeam) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Load BORROW requests
        const borrowsQuery = query(
          collection(db, 'equipmentBorrows'),
          where('teamId', '==', currentTeam.id),
          where('borrowedBy', '==', currentUser.uid),
          where('type', '==', 'BORROW'),
          orderBy('createdAt', 'desc')
        );

        const borrowsSnapshot = await getDocs(borrowsQuery);
        const borrowsData = await Promise.all(
          borrowsSnapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            const equipmentRef = firestoreDoc(db, 'equipment', data.equipmentId);
            const equipmentSnap = await getDoc(equipmentRef);
            const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;

            return {
              id: docSnapshot.id,
              ...data,
              equipment: equipmentData
            };
          })
        );

        // Load RETURN requests
        const returnsQuery = query(
          collection(db, 'equipmentBorrows'),
          where('teamId', '==', currentTeam.id),
          where('returnedBy', '==', currentUser.uid),
          where('type', '==', 'RETURN'),
          orderBy('createdAt', 'desc')
        );

        const returnsSnapshot = await getDocs(returnsQuery);
        const returnsData = await Promise.all(
          returnsSnapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            const equipmentRef = firestoreDoc(db, 'equipment', data.equipmentId);
            const equipmentSnap = await getDoc(equipmentRef);
            const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;

            return {
              id: docSnapshot.id,
              ...data,
              equipment: equipmentData
            };
          })
        );

        setBorrows(borrowsData);
        setReturns(returnsData);
      } catch (err) {
        console.error('Error loading borrows:', err);
        // Fallback: query without orderBy
        try {
          // Load BORROW
          const fallbackBorrowsQuery = query(
            collection(db, 'equipmentBorrows'),
            where('teamId', '==', currentTeam.id),
            where('borrowedBy', '==', currentUser.uid),
            where('type', '==', 'BORROW')
          );
          const borrowsSnapshot = await getDocs(fallbackBorrowsQuery);
          const borrowsData = await Promise.all(
            borrowsSnapshot.docs.map(async (docSnapshot) => {
              const data = docSnapshot.data();
              const equipmentRef = firestoreDoc(db, 'equipment', data.equipmentId);
              const equipmentSnap = await getDoc(equipmentRef);
              const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;

              return {
                id: docSnapshot.id,
                ...data,
                equipment: equipmentData
              };
            })
          );

          // Load RETURN
          const fallbackReturnsQuery = query(
            collection(db, 'equipmentBorrows'),
            where('teamId', '==', currentTeam.id),
            where('returnedBy', '==', currentUser.uid),
            where('type', '==', 'RETURN')
          );
          const returnsSnapshot = await getDocs(fallbackReturnsQuery);
          const returnsData = await Promise.all(
            returnsSnapshot.docs.map(async (docSnapshot) => {
              const data = docSnapshot.data();
              const equipmentRef = firestoreDoc(db, 'equipment', data.equipmentId);
              const equipmentSnap = await getDoc(equipmentRef);
              const equipmentData = equipmentSnap.exists() ? equipmentSnap.data() : null;

              return {
                id: docSnapshot.id,
                ...data,
                equipment: equipmentData
              };
            })
          );

          // Sort in memory
          borrowsData.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
            return dateB - dateA;
          });
          returnsData.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
            return dateB - dateA;
          });
          setBorrows(borrowsData);
          setReturns(returnsData);
        } catch (fallbackErr) {
          console.error('Error loading borrows (fallback):', fallbackErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadBorrows();
  }, [currentUser, currentTeam]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (expectedReturnAt) => {
    if (!expectedReturnAt) return null;
    const returnDate = expectedReturnAt.toDate ? expectedReturnAt.toDate() : new Date(expectedReturnAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    returnDate.setHours(0, 0, 0, 0);
    const diffTime = returnDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusBadge = (status, expectedReturnAt) => {
    const daysRemaining = getDaysRemaining(expectedReturnAt);
    const isOverdue = daysRemaining !== null && daysRemaining < 0;

    if (isOverdue && status === 'APPROVED') {
      return { bg: 'bg-red-100', text: 'text-red-800', label: '⚠️ คืนล่าช้า', icon: '⚠️' };
    }

    const badges = {
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '⏳ รออนุมัติ', icon: '⏳' },
      APPROVED: { bg: 'bg-green-100', text: 'text-green-800', label: '✅ อนุมัติแล้ว', icon: '✅' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-800', label: '❌ ถูกปฏิเสธ', icon: '❌' },
      RETURNED: { bg: 'bg-gray-100', text: 'text-gray-800', label: '📥 คืนแล้ว', icon: '📥' }
    };
    return badges[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: '📋' };
  };

  const handleReturn = (borrow) => {
    setSelectedBorrow(borrow);
    setShowReturnModal(true);
  };

  const handleReturnSuccess = () => {
    setShowReturnModal(false);
    setSelectedBorrow(null);
    // Reload borrows
    window.location.reload();
  };

  // Group by status
  const statusGroups = {
    pendingBorrow: borrows.filter(b => b.status === 'PENDING'),
    approvedBorrow: borrows.filter(b => b.status === 'APPROVED'),
    pendingReturn: returns.filter(r => r.status === 'PENDING'),
    approvedReturn: returns.filter(r => r.status === 'APPROVED')
  };

  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <Link
            to="/nurse/teams"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/nurse/equipment"
              className="text-gray-600 hover:text-gray-800"
            >
              ← กลับ
            </Link>
            <h1 className="text-2xl font-bold text-gray-800">อุปกรณ์ที่ฉันยืม</h1>
          </div>
          <Link
            to="/nurse/equipment/borrow"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + ยืมอุปกรณ์ใหม่
          </Link>
        </div>


        {/* Status Groups */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลด...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* ⏳ รออนุมัติยืม */}
            {statusGroups.pendingBorrow.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  ⏳ รออนุมัติยืม ({statusGroups.pendingBorrow.length})
                </h2>
                <div className="space-y-4">
                  {statusGroups.pendingBorrow.map(borrow => {
              const statusBadge = getStatusBadge(borrow.status, borrow.expectedReturnAt);
              const daysRemaining = getDaysRemaining(borrow.expectedReturnAt);
              const isOverdue = daysRemaining !== null && daysRemaining < 0;

              return (
                <div
                  key={borrow.id}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {borrow.equipment && (
                          <>
                            <span className="text-2xl">🩺</span>
                            <div>
                              <h3 className="font-semibold text-gray-800">{borrow.equipment.name}</h3>
                              <p className="text-sm text-gray-600">{borrow.equipment.code}</p>
                            </div>
                          </>
                        )}
                        <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                          {statusBadge.label}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500">วันที่ยืม</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formatDate(borrow.borrowDate || borrow.createdAt)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">วันที่คืนที่คาดหวัง</p>
                          <p className={`text-sm font-medium ${
                            isOverdue ? 'text-red-600' : daysRemaining !== null && daysRemaining <= 3 ? 'text-orange-600' : 'text-gray-800'
                          }`}>
                            {formatDate(borrow.expectedReturnAt)}
                            {daysRemaining !== null && (
                              <span className="ml-2">
                                ({isOverdue ? `ล่าช้า ${Math.abs(daysRemaining)} วัน` : `เหลืออีก ${daysRemaining} วัน`})
                              </span>
                            )}
                          </p>
                        </div>
                        {borrow.approvedAt && (
                          <div>
                            <p className="text-xs text-gray-500">อนุมัติเมื่อ</p>
                            <p className="text-sm font-medium text-gray-800">
                              {formatDateTime(borrow.approvedAt)}
                            </p>
                          </div>
                        )}
                        {borrow.rejectedAt && (
                          <div>
                            <p className="text-xs text-gray-500">ปฏิเสธเมื่อ</p>
                            <p className="text-sm font-medium text-gray-800">
                              {formatDateTime(borrow.rejectedAt)}
                            </p>
                            {borrow.rejectionReason && (
                              <p className="text-xs text-red-600 mt-1">
                                เหตุผล: {borrow.rejectionReason}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {borrow.notes && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">หมายเหตุ:</p>
                          <p className="text-sm text-gray-700">{borrow.notes}</p>
                        </div>
                      )}
                    </div>

                    <div className="ml-4 flex flex-col gap-2">
                      {borrow.status === 'APPROVED' && (
                        <button
                          onClick={() => handleReturn(borrow)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                        >
                          📥 คืนอุปกรณ์
                        </button>
                      )}
                      {borrow.status === 'PENDING' && (
                        <button
                          onClick={() => {
                            if (window.confirm('ต้องการยกเลิกคำขอยืมนี้หรือไม่?')) {
                              // TODO: Implement cancel borrow
                              alert('ฟีเจอร์ยกเลิกคำขอจะพัฒนาต่อ');
                            }
                          }}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium"
                        >
                          ยกเลิกคำขอ
                        </button>
                      )}
                      {borrow.equipment && (
                        <Link
                          to={`/nurse/equipment/${borrow.equipmentId}`}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                        >
                          ดูรายละเอียด
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ✅ กำลังยืม */}
            {statusGroups.approvedBorrow.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  ✅ กำลังยืม ({statusGroups.approvedBorrow.length})
                </h2>
                <div className="space-y-4">
                  {statusGroups.approvedBorrow.map(borrow => {
                    const statusBadge = getStatusBadge(borrow.status, borrow.expectedReturnAt);
                    const daysRemaining = getDaysRemaining(borrow.expectedReturnAt);
                    const isOverdue = daysRemaining !== null && daysRemaining < 0;

                    return (
                      <div
                        key={borrow.id}
                        className={`bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition ${
                          isOverdue ? 'border-l-4 border-red-500' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                                {statusBadge.label}
                              </span>
                              {borrow.equipment && (
                                <>
                                  <span className="text-2xl">🩺</span>
                                  <div>
                                    <h3 className="font-semibold text-gray-800">{borrow.equipment.name}</h3>
                                    <p className="text-sm text-gray-600">{borrow.equipment.code}</p>
                                  </div>
                                </>
                              )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div>
                                <p className="text-xs text-gray-500">📅 วันที่ยืม</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {formatDate(borrow.borrowDate || borrow.createdAt)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">📅 วันที่คืนที่คาดหวัง</p>
                                <p className={`text-sm font-medium ${
                                  isOverdue ? 'text-red-600' : daysRemaining !== null && daysRemaining <= 3 ? 'text-orange-600' : 'text-gray-800'
                                }`}>
                                  {formatDate(borrow.expectedReturnAt)}
                                  {daysRemaining !== null && (
                                    <span className="ml-2">
                                      ({isOverdue ? `ล่าช้า ${Math.abs(daysRemaining)} วัน` : `เหลืออีก ${daysRemaining} วัน`})
                                    </span>
                                  )}
                                </p>
                              </div>
                              {borrow.approvedAt && (
                                <div>
                                  <p className="text-xs text-gray-500">✅ อนุมัติเมื่อ</p>
                                  <p className="text-sm font-medium text-gray-800">
                                    {formatDateTime(borrow.approvedAt)}
                                  </p>
                                </div>
                              )}
                            </div>

                            {borrow.notes && (
                              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">หมายเหตุ:</p>
                                <p className="text-sm text-gray-700">{borrow.notes}</p>
                              </div>
                            )}
                          </div>

                          <div className="ml-4 flex flex-col gap-2">
                            <button
                              onClick={() => handleReturn(borrow)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                            >
                              📥 คืนอุปกรณ์
                            </button>
                            {borrow.equipment && (
                              <Link
                                to={`/nurse/equipment/${borrow.equipmentId}`}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                              >
                                👁️ ดูรายละเอียด
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 📥 รออนุมัติคืน */}
            {statusGroups.pendingReturn.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  📥 รออนุมัติคืน ({statusGroups.pendingReturn.length})
                </h2>
                <div className="space-y-4">
                  {statusGroups.pendingReturn.map(returnItem => {
                    return (
                      <div
                        key={returnItem.id}
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {returnItem.equipment && (
                                <>
                                  <span className="text-2xl">🩺</span>
                                  <div>
                                    <h3 className="font-semibold text-gray-800">{returnItem.equipment.name || returnItem.equipmentName}</h3>
                                    <p className="text-sm text-gray-600">{returnItem.equipment.code || returnItem.equipmentCode}</p>
                                  </div>
                                </>
                              )}
                              <span className="px-3 py-1 rounded-lg text-sm font-semibold bg-blue-100 text-blue-800">
                                ⏳ รอการอนุมัติจากหัวหน้าทีม
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div>
                                <p className="text-xs text-gray-500">📅 ส่งคำขอคืน</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {formatDateTime(returnItem.createdAt)}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">📊 สภาพอุปกรณ์</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {returnItem.returnCondition === 'GOOD' ? '✅ ดี' :
                                   returnItem.returnCondition === 'DAMAGED' ? '⚠️ เสียหาย' :
                                   returnItem.returnCondition === 'NEEDS_REPAIR' ? '🔧 ต้องซ่อม' : '-'}
                                </p>
                              </div>
                            </div>

                            {returnItem.returnDescription && (
                              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs text-gray-500 mb-1">รายละเอียด:</p>
                                <p className="text-sm text-gray-700">{returnItem.returnDescription}</p>
                              </div>
                            )}
                          </div>

                          <div className="ml-4 flex flex-col gap-2">
                            {returnItem.equipment && (
                              <Link
                                to={`/nurse/equipment/${returnItem.equipmentId}`}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                              >
                                👁️ ดูรายละเอียด
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ✅ คืนแล้ว */}
            {statusGroups.approvedReturn.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  ✅ คืนแล้ว ({statusGroups.approvedReturn.length})
                </h2>
                <div className="space-y-4">
                  {statusGroups.approvedReturn.map(returnItem => {
                    return (
                      <div
                        key={returnItem.id}
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {returnItem.equipment && (
                                <>
                                  <span className="text-2xl">🩺</span>
                                  <div>
                                    <h3 className="font-semibold text-gray-800">{returnItem.equipment.name || returnItem.equipmentName}</h3>
                                    <p className="text-sm text-gray-600">{returnItem.equipment.code || returnItem.equipmentCode}</p>
                                  </div>
                                </>
                              )}
                              <span className="px-3 py-1 rounded-lg text-sm font-semibold bg-green-100 text-green-800">
                                ✅ คืนแล้ว
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div>
                                <p className="text-xs text-gray-500">📅 วันที่คืน</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {formatDate(returnItem.actualReturnDate || returnItem.createdAt)}
                                </p>
                              </div>
                              {returnItem.approvedAt && (
                                <div>
                                  <p className="text-xs text-gray-500">✅ อนุมัติเมื่อ</p>
                                  <p className="text-sm font-medium text-gray-800">
                                    {formatDateTime(returnItem.approvedAt)}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="ml-4 flex flex-col gap-2">
                            {returnItem.equipment && (
                              <Link
                                to={`/nurse/equipment/${returnItem.equipmentId}`}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                              >
                                👁️ ดูรายละเอียด
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {statusGroups.pendingBorrow.length === 0 &&
             statusGroups.approvedBorrow.length === 0 &&
             statusGroups.pendingReturn.length === 0 &&
             statusGroups.approvedReturn.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <div className="text-6xl mb-4">📦</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">ไม่มีรายการ</h3>
                <p className="text-gray-600 mb-4">คุณยังไม่มีการยืมอุปกรณ์</p>
                <Link
                  to="/nurse/equipment/borrow"
                  className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  + ยืมอุปกรณ์ใหม่
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Return Modal */}
      {showReturnModal && selectedBorrow && (
        <ReturnEquipmentModal
          isOpen={showReturnModal}
          onClose={() => {
            setShowReturnModal(false);
            setSelectedBorrow(null);
          }}
          onSuccess={handleReturnSuccess}
          borrow={selectedBorrow}
        />
      )}
    </div>
  );
};

export default MyBorrowedEquipmentPage;

